// src/pages/admin/AdminSettingsPage.jsx
// Page unifiée de paramètres admin
// Sections : Pack par défaut · Crédits · Règles de réservation

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Card, CardContent, CardHeader, Chip, CircularProgress,
  Divider, FormControl, FormControlLabel, InputLabel, MenuItem,
  Select, Stack, Switch, Typography, Button, Alert, Tooltip,
  IconButton, LinearProgress, TextField, InputAdornment,
  ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import SettingsIcon         from "@mui/icons-material/Settings";
import AutorenewIcon        from "@mui/icons-material/Autorenew";
import CreditCardIcon       from "@mui/icons-material/CreditCard";
import EmailIcon            from "@mui/icons-material/Email";
import SaveIcon             from "@mui/icons-material/Save";
import RefreshIcon          from "@mui/icons-material/Refresh";
import CheckCircleIcon      from "@mui/icons-material/CheckCircle";
import CancelIcon           from "@mui/icons-material/Cancel";
import InfoOutlinedIcon     from "@mui/icons-material/InfoOutlined";
import Visibility           from "@mui/icons-material/Visibility";
import VisibilityOff        from "@mui/icons-material/VisibilityOff";
import SendIcon             from "@mui/icons-material/Send";
import { useSnackbar }      from "notistack";
import { useTranslation }   from "react-i18next";
import api                  from "../../api/axios";
import { getNetbotConfig, updateNetbotConfig, getNetbotProviders } from "../../api/adminNetbotApi";
import { AdminSettingsAPI } from "../../api/adminSettingsApi";

// ─────────────────────────────────────────────────────────────────────────────
// Section 1 — Pack par défaut
// ─────────────────────────────────────────────────────────────────────────────
function DefaultPackSection() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [packs,      setPacks]      = useState([]);
  const [current,    setCurrent]    = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingRes, packsRes] = await Promise.all([
        api.get("/admin/settings/default-pack"),
        api.get("/admin/packages/packs", { params: { limit: 200 } }),
      ]);
      const setting = settingRes.data;
      const items   = packsRes.data?.items || [];
      setCurrent(setting);
      setSelectedId(setting.pack_id ? String(setting.pack_id) : "");
      setPacks(items.filter(p => p.is_active));
    } catch {
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => { load(); }, [load]);

  const isDirty = String(current?.pack_id || "") !== selectedId;

  const handleSave = async () => {
    setSaving(true);
    try {
      const pack_id = selectedId ? Number(selectedId) : null;
      await api.put("/admin/settings/default-pack", { pack_id });
      enqueueSnackbar(
        pack_id ? t("admin.default_pack.saved") : t("admin.default_pack.removed"),
        { variant: "success" }
      );
      await load();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.detail || t("snackbar.error"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/default-pack", { pack_id: null });
      setSelectedId("");
      enqueueSnackbar(t("admin.default_pack.removed"), { variant: "success" });
      await load();
    } catch {
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const selectedPack = packs.find(p => String(p.id) === selectedId);

  if (loading) return (
    <Box sx={{ py: 2 }}>
      <LinearProgress sx={{ borderRadius: 1 }} />
    </Box>
  );

  return (
    <Stack spacing={2.5}>
      {/* Pack actuel */}
      {current?.pack ? (
        <Box sx={{ bgcolor: "#f0fdf4", borderRadius: 2, p: 2, border: "1px solid #86efac" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} />
              <Box>
                <Typography variant="body2" fontWeight={700} sx={{ color: "#166534" }}>
                  {current.pack.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {current.pack.total_minutes} min
                  {current.pack.validity_days
                    ? ` · expire dans ${current.pack.validity_days} jours`
                    : " · sans expiration"}
                  {" · "}Code : {current.pack.code}
                </Typography>
              </Box>
            </Stack>
            <Tooltip title="Supprimer le pack par défaut">
              <IconButton size="small" onClick={handleRemove} disabled={saving}
                sx={{ color: "#dc2626", "&:hover": { bgcolor: "#fef2f2" } }}>
                <CancelIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ bgcolor: "#fffbeb", borderRadius: 2, p: 2, border: "1px solid #fde68a" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlinedIcon sx={{ fontSize: 15, color: "#b45309" }} />
            <Typography variant="caption" sx={{ color: "#92400e" }}>
              {t("admin.default_pack.none_configured")}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Sélecteur */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-end" }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>{t("admin.default_pack.select_label")}</InputLabel>
          <Select value={selectedId} label={t("admin.default_pack.select_label")}
            onChange={e => setSelectedId(e.target.value)}
            sx={{ borderRadius: 2 }}>
            <MenuItem value=""><em>{t("admin.default_pack.none_option")}</em></MenuItem>
            {packs.map(p => (
              <MenuItem key={p.id} value={String(p.id)}>
                <Stack direction="row" justifyContent="space-between"
                  alignItems="center" sx={{ width: "100%" }}>
                  <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                  <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                    <Chip size="small" label={`${p.total_minutes} min`}
                      sx={{ height: 16, fontSize: 10, bgcolor: "#eef2ff", color: "#6366f1" }} />
                    {p.validity_days && (
                      <Typography variant="caption" color="text.secondary">{p.validity_days}j</Typography>
                    )}
                  </Stack>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" onClick={handleSave}
          disabled={saving || !isDirty}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" },
            "&.Mui-disabled": { bgcolor: "#e2e8f0" }, whiteSpace: "nowrap" }}>
          {saving ? "Enregistrement…" : t("admin.default_pack.save")}
        </Button>
      </Stack>

      {/* Prévisualisation */}
      {selectedPack && isDirty && (
        <Box sx={{ bgcolor: "#f0f9ff", borderRadius: 2, p: 1.5, border: "1px solid #bae6fd" }}>
          <Typography variant="caption" sx={{ color: "#0369a1" }}>
            Après enregistrement : chaque nouveau compte recevra{" "}
            <strong>{selectedPack.total_minutes} min</strong> — Pack{" "}
            <strong>{selectedPack.name}</strong>
            {selectedPack.validity_days
              ? `, valable ${selectedPack.validity_days} jours`
              : ", sans expiration"}.
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 2 — Paramètres crédits (mode de débit + stacking)
// ─────────────────────────────────────────────────────────────────────────────
function CreditsSettingsSection() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [mode,     setMode]     = useState("reserved");
  const [stacking, setStacking] = useState(true);
  const [initial,  setInitial]  = useState({ mode: "reserved", stacking: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AdminSettingsAPI.getCreditsSettings();
      const m = data?.credit_debit_mode || "reserved";
      const s = !!data?.allow_pack_stacking;
      setMode(m); setStacking(s);
      setInitial({ mode: m, stacking: s });
    } catch {
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => { load(); }, [load]);

  const isDirty = mode !== initial.mode || stacking !== initial.stacking;

  const save = async () => {
    setSaving(true);
    try {
      await AdminSettingsAPI.updateCreditsSettings({
        credit_debit_mode: mode,
        allow_pack_stacking: stacking,
      });
      setInitial({ mode, stacking });
      enqueueSnackbar(t("admin.creditsSettings.snack.saved"), { variant: "success" });
    } catch {
      enqueueSnackbar(t("snackbar.error"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LinearProgress sx={{ borderRadius: 1, mt: 1 }} />;

  return (
    <Stack spacing={3}>
      {/* Mode de débit */}
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="body2" fontWeight={700}>
            {t("admin.creditsSettings.sections.debitMode.title")}
          </Typography>
          <Tooltip title={t("admin.creditsSettings.sections.debitMode.help")}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          {t("admin.creditsSettings.hints.defaultIsReserved")}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>{t("admin.creditsSettings.fields.debitMode")}</InputLabel>
          <Select value={mode} label={t("admin.creditsSettings.fields.debitMode")}
            onChange={e => setMode(e.target.value)} sx={{ borderRadius: 2 }}>
            <MenuItem value="reserved">
              <Stack>
                <Typography variant="body2">{t("admin.creditsSettings.debitMode.reserved")}</Typography>
              </Stack>
            </MenuItem>
            <MenuItem value="consumed">
              <Typography variant="body2">{t("admin.creditsSettings.debitMode.consumed")}</Typography>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider />

      {/* Stacking */}
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="body2" fontWeight={700}>
            {t("admin.creditsSettings.sections.stacking.title")}
          </Typography>
          <Tooltip title={t("admin.creditsSettings.sections.stacking.help")}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {t("admin.creditsSettings.stacking.recommendation")}
        </Typography>
        <FormControlLabel
          control={
            <Switch checked={stacking} onChange={e => setStacking(e.target.checked)}
              sx={{ "& .MuiSwitch-thumb": { bgcolor: stacking ? "#6366f1" : undefined } }} />
          }
          label={
            <Typography variant="body2">
              {t("admin.creditsSettings.fields.allowStacking")}
              {" "}
              <Chip size="small" label={stacking ? "Activé" : "Désactivé"}
                sx={{ height: 18, fontSize: 10,
                  bgcolor: stacking ? "#eef2ff" : "#f8fafc",
                  color: stacking ? "#6366f1" : "#64748b" }} />
            </Typography>
          }
        />
      </Box>

      {/* Bouton save */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button variant="contained" onClick={save}
          disabled={saving || !isDirty}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" },
            "&.Mui-disabled": { bgcolor: "#e2e8f0" } }}>
          {saving ? "Enregistrement…" : t("common.save")}
        </Button>
        <Button size="small" onClick={load} startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
          sx={{ borderRadius: 2, color: "text.secondary" }}>
          {t("common.refresh")}
        </Button>
        {isDirty && (
          <Typography variant="caption" sx={{ color: "#f59e0b" }}>
            ● {t("admin.creditsSettings.hints.dirty")}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page principale
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Section 3 — Paramètres mail
// ─────────────────────────────────────────────────────────────────────────────
function MailSettingsSection() {
  const { enqueueSnackbar } = useSnackbar();

  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [cfg,      setCfg]      = useState({});
  const [showPwd,  setShowPwd]  = useState(false);
  const [showTok,  setShowTok]  = useState(false);
  const [source,   setSource]   = useState("env");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings/mail");
      setCfg(data);
      setSource(data._source || "env");
    } catch {
      enqueueSnackbar("Erreur chargement config mail", { variant: "error" });
    } finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setCfg(prev => ({ ...prev, [key]: val }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/mail", cfg);
      setCfg(data);
      setSource(data._source || "db");
      enqueueSnackbar("Configuration mail enregistrée ✅", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.detail || "Erreur sauvegarde", { variant: "error" });
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/settings/mail/test");
      enqueueSnackbar(`Email de test envoyé à ${data.sent_to} ✅`, { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.detail || "Échec du test", { variant: "error" });
    } finally { setTesting(false); }
  };

  if (loading) return <LinearProgress sx={{ borderRadius: 1, mt: 1 }} />;

  const isPostmark = cfg.provider === "postmark";

  return (
    <Stack spacing={3}>
      {/* Badge source */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small"
          label={source === "db" ? "Configuré via l'admin (DB)" : "Valeurs depuis .env"}
          sx={{
            bgcolor: source === "db" ? "#eef2ff" : "#fffbeb",
            color:   source === "db" ? "#6366f1" : "#b45309",
            fontWeight: 600, fontSize: 11,
          }} />
        {source === "env" && (
          <Typography variant="caption" color="text.secondary">
            Enregistrez pour surcharger le fichier .env
          </Typography>
        )}
      </Stack>

      {/* Activé / désactivé */}
      <FormControlLabel
        control={
          <Switch checked={Boolean(cfg.enabled)} onChange={set("enabled")}
            sx={{ "& .MuiSwitch-thumb": { bgcolor: cfg.enabled ? "#6366f1" : undefined } }} />
        }
        label={
          <Typography variant="body2" fontWeight={600}>
            Email activé
            <Chip size="small"
              label={cfg.enabled ? "Activé" : "Désactivé"}
              sx={{ ml: 1, height: 18, fontSize: 10,
                bgcolor: cfg.enabled ? "#f0fdf4" : "#f8fafc",
                color: cfg.enabled ? "#16a34a" : "#64748b" }} />
          </Typography>
        }
      />

      <Divider />

      {/* Provider */}
      <Box>
        <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5 }}>
          Fournisseur d'envoi
        </Typography>
        <ToggleButtonGroup
          value={cfg.provider || "smtp"} exclusive
          onChange={(_, v) => v && setCfg(prev => ({ ...prev, provider: v }))}
          size="small">
          <ToggleButton value="smtp" sx={{ px: 3, textTransform: "none", fontSize: 13 }}>
            SMTP classique
          </ToggleButton>
          <ToggleButton value="postmark" sx={{ px: 3, textTransform: "none", fontSize: 13 }}>
            Postmark HTTP API
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Adresse expéditeur + alerte */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label="Adresse expéditeur (From)"
          size="small" fullWidth value={cfg.from_address || ""}
          onChange={set("from_address")}
          placeholder="NextHopLab <no-reply@domaine.com>"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          helperText="Format : Nom Affiché <email@domaine.com>"
        />
        <TextField
          label="Email alertes système"
          size="small" fullWidth value={cfg.alert_email || ""}
          onChange={set("alert_email")}
          placeholder="admin@domaine.com"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          helperText="Reçoit les alertes GNS3 DOWN"
        />
      </Stack>

      <Divider />

      {/* Config SMTP */}
      {!isPostmark && (
        <Stack spacing={2}>
          <Typography variant="body2" fontWeight={700} color="text.secondary">
            Configuration SMTP
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Serveur SMTP (host)" size="small" fullWidth
              value={cfg.smtp_host || ""} onChange={set("smtp_host")}
              placeholder="smtp.postmarkapp.com"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <TextField label="Port" size="small" sx={{ width: 110, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              value={cfg.smtp_port || 587} onChange={set("smtp_port")}
              type="number" inputProps={{ min: 1, max: 65535 }} />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField label="Nom d'utilisateur SMTP" size="small" fullWidth
              value={cfg.smtp_username || ""} onChange={set("smtp_username")}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <TextField label="Mot de passe SMTP"
              size="small" fullWidth
              type={showPwd ? "text" : "password"}
              value={cfg.smtp_password || ""} onChange={set("smtp_password")}
              placeholder="Laisser vide = inchangé"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPwd(p => !p)}>
                      {showPwd ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={<Switch checked={Boolean(cfg.smtp_use_tls)} onChange={set("smtp_use_tls")} size="small" />}
              label={<Typography variant="body2">STARTTLS</Typography>} />
            <FormControlLabel
              control={<Switch checked={Boolean(cfg.smtp_use_ssl)} onChange={set("smtp_use_ssl")} size="small" />}
              label={<Typography variant="body2">SSL/TLS direct</Typography>} />
          </Stack>
        </Stack>
      )}

      {/* Config Postmark */}
      {isPostmark && (
        <Stack spacing={2}>
          <Typography variant="body2" fontWeight={700} color="text.secondary">
            Postmark HTTP API
          </Typography>
          <TextField
            label="Postmark Server API Token"
            size="small" fullWidth
            type={showTok ? "text" : "password"}
            value={cfg.postmark_token || ""} onChange={set("postmark_token")}
            placeholder="Laisser vide = inchangé"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowTok(p => !p)}>
                    {showTok ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            helperText="Trouvez votre token dans Postmark → Servers → API Tokens"
          />
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: 12 }}>
            L'adresse <strong>From</strong> doit être une Sender Signature vérifiée dans Postmark.
          </Alert>
        </Stack>
      )}

      {/* Actions */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Button variant="contained" onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
        <Button variant="outlined" onClick={handleTest}
          disabled={testing || !cfg.enabled}
          startIcon={testing ? <CircularProgress size={14} /> : <SendIcon />}
          sx={{ borderRadius: 2 }}>
          {testing ? "Envoi…" : "Envoyer un email de test"}
        </Button>
        <Button size="small" onClick={load} startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
          sx={{ borderRadius: 2, color: "text.secondary" }}>
          Actualiser
        </Button>
      </Stack>
    </Stack>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Section AI — Configuration Claude / NetBot
// ─────────────────────────────────────────────────────────────────────────────
function AISettingsSection() {
  const { enqueueSnackbar } = useSnackbar();
  const [cfg,     setCfg]     = useState({ enabled: false, provider: "claude", model: "claude-sonnet-4-20250514", modes: [], api_key: "" });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [showKey, setShowKey] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/settings/ai"); setCfg(r.data || {}); }
    catch(e) { enqueueSnackbar("Erreur chargement config AI", { variant: "error" }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/ai", cfg);
      enqueueSnackbar("Configuration AI enregistree", { variant: "success" });
    } catch(e) { enqueueSnackbar("Erreur sauvegarde", { variant: "error" }); }
    finally { setSaving(false); }
  };

  const MODES = ["lab_debugger", "course_tutor", "exam_assistant"];

  if (loading) return <LinearProgress sx={{ borderRadius: 1 }} />;

  return (
    <Stack spacing={3}>
      <FormControlLabel
        control={<Switch checked={Boolean(cfg.enabled)} onChange={e => setCfg(c => ({ ...c, enabled: e.target.checked }))} />}
        label={<Typography variant="body2" fontWeight={600}>AI {cfg.enabled ? "Activee" : "Desactivee"}</Typography>} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Provider</InputLabel>
          <Select value={cfg.provider || "claude"} onChange={e => setCfg(c => ({ ...c, provider: e.target.value }))} label="Provider">
            <MenuItem value="claude">Claude (Anthropic)</MenuItem>
            <MenuItem value="openai">OpenAI</MenuItem>
            <MenuItem value="mistral">Mistral AI</MenuItem>
            <MenuItem value="ollama">Ollama (local)</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Modele</InputLabel>
          <Select value={cfg.model || ""} onChange={e => setCfg(c => ({ ...c, model: e.target.value }))} label="Modele">
            <MenuItem value="claude-sonnet-4-20250514">Claude Sonnet 4</MenuItem>
            <MenuItem value="claude-opus-4-6">Claude Opus 4.6</MenuItem>
            <MenuItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5</MenuItem>
            <MenuItem value="gpt-4o">GPT-4o</MenuItem>
            <MenuItem value="mistral-large-latest">Mistral Large</MenuItem>
            <MenuItem value="tinyllama">Ollama - tinyllama</MenuItem><MenuItem value="llama3">Ollama - llama3</MenuItem><MenuItem value="mistral">Ollama - mistral</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <TextField
        label="Cle API"
        size="small" fullWidth
        type={showKey ? "text" : "password"}
        value={cfg.api_key || ""}
        onChange={e => setCfg(c => ({ ...c, api_key: e.target.value }))}
        placeholder="Laisser vide = inchange"
        InputProps={{ endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShowKey(p => !p)}>
              {showKey ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
            </IconButton>
          </InputAdornment>
        )}}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />

      <Box>
        <Typography variant="body2" fontWeight={600} mb={1}>Modes actives</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {MODES.map(m => (
            <Chip key={m} label={m}
              color={(cfg.modes || []).includes(m) ? "primary" : "default"}
              onClick={() => setCfg(c => ({
                ...c,
                modes: (c.modes || []).includes(m)
                  ? (c.modes || []).filter(x => x !== m)
                  : [...(c.modes || []), m]
              }))}
              sx={{ cursor: "pointer", borderRadius: 2 }} />
          ))}
        </Stack>
      </Box>

      <Stack direction="row" spacing={1.5}>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          sx={{ borderRadius: 2, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
          {saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
        <Button size="small" onClick={load} startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
          sx={{ borderRadius: 2, color: "text.secondary" }}>Actualiser</Button>
      </Stack>
    </Stack>
  );
}
const SECTIONS = [
  {
    id:    "default_pack",
    icon:  <AutorenewIcon sx={{ fontSize: 20, color: "#6366f1" }} />,
    titleKey: "admin.default_pack.title",
    subtitleKey: "admin.default_pack.subtitle",
    component: <DefaultPackSection />,
  },
  {
    id:    "credits",
    icon:  <CreditCardIcon sx={{ fontSize: 20, color: "#6366f1" }} />,
    titleKey: "admin.creditsSettings.title",
    subtitleKey: "admin.creditsSettings.subtitle",
    component: <CreditsSettingsSection />,
  },
  {
    id:    "mail",
    icon:  <EmailIcon sx={{ fontSize: 20, color: "#6366f1" }} />,
    titleKey: "admin.mailSettings.title",
    subtitleKey: "admin.mailSettings.subtitle",
    component: <MailSettingsSection />,
  },
  {
    id:    "ai",
    icon:  <SettingsIcon sx={{ fontSize: 20, color: "#6366f1" }} />,
    titleKey: "admin.ai.title",
    subtitleKey: "admin.ai.subtitle",
    component: <AISettingsSection />,
  },
];

export default function AdminSettingsPage() {
  const { t } = useTranslation();

  return (
    <Box sx={{ maxWidth: 860, mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
      {/* En-tête */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 4 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "#eef2ff",
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <SettingsIcon sx={{ color: "#6366f1", fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {t("nav.admin_settings")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Paramètres globaux de la plateforme
          </Typography>
        </Box>
      </Stack>

      {/* Sections */}
      <Stack spacing={3}>
        {SECTIONS.map(section => (
          <Card key={section.id} elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <CardHeader
              avatar={
                <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#eef2ff",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {section.icon}
                </Box>
              }
              title={
                <Typography variant="subtitle1" fontWeight={700}>
                  {t(section.titleKey)}
                </Typography>
              }
              subheader={
                <Typography variant="caption" color="text.secondary">
                  {t(section.subtitleKey)}
                </Typography>
              }
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 2.5 }}>
              {section.component}
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}



